<template>
  <div :class="$style.wrapper">
    <section :class="$style.section">
      <h2 :class="[$style['heading-secondary'], $style.underline]">
        How Does Funding Work?
      </h2>
      <EmbedProcessSteps :steps="processSteps" />
    </section>
    <section
      v-if="advertisedRates && advertisedRates.rates"
      :class="$style.section"
    >
      <h2 :class="$style['heading-secondary']">
        <span :class="$style.underline">What are the Estimated Fees?</span
        ><span :class="$style['heading-secondary__disclaimer']">*</span>
      </h2>
      <EmbedCalculatorPage />
      <p :class="$style.disclaimer">
        * The Extension Plans shown here are indicative amounts, and based on
        the information you have provided to us, as well as the typical fees we
        charge for the corresponding extension period. Additional terms and
        conditions apply. You may not be approved for your preferred Extension
        Plan, and your fees may be different based on the circumstances of your
        business. Funding is conditional on successful application. If approved,
        you will be informed of the option we may be able to provide to you, and
        you will have an opportunity to approve/reject such option. All currency
        conversion amounts are indicative amounts based on current rates, and
        are subject to change. Additionally, FX commissions may apply. All
        amounts are finalized prior to contract execution.
      </p>
    </section>
    <section :class="$style.section">
      <h2 :class="[$style['heading-secondary'], $style.underline]">
        What Can I Fund?
      </h2>
      <button
        :class="[$style.toggle, toggleActive ? $style['toggle--right'] : '']"
        @click="toggleFundType"
      >
        <span :class="$style.toggle__label">Invoices</span>
        <span :class="$style.toggle__label">Cash Advances</span>
      </button>
      <transition name="fade" mode="out-in">
        <EmbedAcceptanceCriteria
          v-if="!toggleActive"
          :content="acceptanceInvoices"
        />
        <EmbedAcceptanceCriteria v-else :content="acceptanceCashAdvances" />
      </transition>
      <p :class="$style.disclaimer">
        * Subject to approval. Additional terms and conditions apply.
      </p>
    </section>
  </div>
</template>

<script>
import EmbedProcessSteps from '@/pages/embed/components/EmbedProcessSteps.vue';
import EmbedCalculatorPage from '@/pages/embed/EmbedCalculatorPage.vue';
import EmbedAcceptanceCriteria from '@/pages/embed/components/EmbedAcceptanceCriteria.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    EmbedProcessSteps,
    EmbedCalculatorPage,
    EmbedAcceptanceCriteria,
  },
  data() {
    return {
      toggleActive: false,
      processSteps: [
        {
          description:
            'Your funding capacity is calculated from your connected bank and revenue accounts.',
          icon: 'query_stats',
        },
        {
          description:
            'Once approved, funds remain available for 30 days and can be used to pay invoices or as a Cash Advance.',
          icon: 'price_change',
        },
        {
          description: 'Repay weekly over time via automatic direct debits.',
          icon: 'credit_score',
        },
      ],
      acceptanceInvoices: {
        criteria: [
          'Invoice cannot be more than 60 days overdue',
          'Invoice must be addressed to the customer we are funding',
          'Vendor name, country of operation and description of spend must be present',
          'Amount submitted for funding is equal or less than the total amount denoted on the invoice',
          'Payment must be issued in one of our supported currencies (EUR, GBP, CAD, USD)',
        ],
        categories: [
          'Inventory',
          'Marketing',
          'Shipping and Logistics',
          'Professional Services',
          'Other',
        ],
        categoriesLink: '',
        ctaLabel: 'Read more details here',
        ctaLink:
          'https://help.clear.co/s/article/How-to-submit-an-invoice?language=en_US',
      },
      acceptanceCashAdvances: {
        criteria: [
          'No invoices or receipts required',
          'Funds are sent only to the connected primary business bank account',
          'Funds are sent in USD only',
          'Limits: Minimum $10,000; Maximum is the lower of $600,000 per advance or remaining capacity',
        ],
        categories: [
          'Inventory',
          'Marketing',
          'Shipping and Logistics',
          'Professional Services',
          'Other',
        ],
        categoriesLink: '',
        ctaLabel: 'Read more details here',
        ctaLink:
          'https://help.clear.co/s/article/How-to-apply-for-a-Cash-Advance?language=en_US',
      },
    };
  },
  computed: {
    ...mapGetters(['advertisedRates']),
  },
  async mounted() {
    await this.$store.dispatchApiAction('GET_ADVERTISED_FEES');
  },
  methods: {
    toggleFundType() {
      this.toggleActive = !this.toggleActive;
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="less" module>
.wrapper {
  max-width: 90%;
  margin: 0 auto;
}

@media screen and (min-width: 500px) {
  .wrapper {
    max-width: 85%;
  }
}

.section {
  padding: 50px 0 60px;
}

.section + .section {
  border-top: 1px solid #e2e2e2;
  box-shadow: inset 0 1px 0 #fff;
}

.underline {
  text-decoration-line: underline;
}

.wrapper h2.heading-secondary {
  color: #111111;
  font-family: @regular-font;
  font-size: 28px;
  font-style: normal;
  font-weight: 800;
  line-height: 150%;
  margin: -10px 0 36px 0;
  position: relative;
}

.heading-secondary__disclaimer {
  color: #767676;
  transform: translateX(3px);
  display: inline-block;
}

.disclaimer {
  color: #767676;
  font-family: @regular-font;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 19.5px */
  margin: 0;
}

.toggle {
  background: #fff;
  border-radius: 25px;
  display: inline-flex;
  position: relative;
  margin-bottom: 37px;
}

.toggle:before {
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: #7f30ff;
  content: '';
  border-radius: 25px;
  transform: translateZ(-1px);
  transition:
    transform 0.6s cubic-bezier(0.76, 0, 0.24, 1),
    background 0.2s;
}

.toggle:hover:before {
  background: #5b00f0;
}

.toggle--right:before {
  transform: translateX(100%);
}

.toggle__label {
  color: #111;
  font-family: @regular-font;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 1; /* 27px */
  padding: 16px 40px;
  transform-style: preserve-3d;
  transition: color 0.6s;
  margin: 0px 20px 0px 40px;
}

.toggle__label:first-child {
  color: #fff;
}

.toggle--right .toggle__label:first-child {
  color: #111;
}

.toggle--right .toggle__label:last-child {
  color: #fff;
}
</style>
